// src/pages/Blog.jsx
import React, { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import {
  ArrowRight,
  Star,
  Clock,
  Users,
  Calendar,
  Mail,
  TrendingUp,
  ExternalLink,
} from "lucide-react";

import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import {
  ARTICLES,
  FEATURED_STORY,
  getCategories,
} from "../data/articles/articles";
import { PRESS_LINKS } from "../data/articles/press";

const SITE = {
  name: "D10 Magazine",
  baseUrl: "https://d10.com", // ✅ change to your real domain
  blogTitle: "D10 Journal",
  blogDescription:
    "Stories from Pag: pizza, football, ingredients, and island life — D10's editorial corner.",
};

/* =========================
   HELPERS
========================= */

/* Build a safe, deduped list of stories that includes FEATURED_STORY */
const buildAllStories = () => {
  const base = Array.isArray(ARTICLES) ? [...ARTICLES] : [];

  // include featured story (at least present in list)
  if (FEATURED_STORY && FEATURED_STORY.slug) {
    const exists = base.some((a) => a?.slug === FEATURED_STORY.slug);
    if (!exists) base.unshift(FEATURED_STORY);
  }

  // remove nulls + ensure unique by slug
  const seen = new Set();
  return base.filter((a) => {
    if (!a || !a.slug) return false;
    if (seen.has(a.slug)) return false;
    seen.add(a.slug);
    return true;
  });
};
const normalizeCategory = (c) => String(c || "").trim();
const isSameCategory = (a, b) => normalizeCategory(a) === normalizeCategory(b);

// ✅ ADD THIS
const getSortTs = (a) => {
  // Prefer ISO dates (best)
  const iso =
    a?.datePublished ||
    a?.dateModified ||
    a?.publishedAt ||
    null;

  if (iso) {
    const t = Date.parse(iso);
    if (!Number.isNaN(t)) return t;
  }

  // Fallback: try human date like "Sep 19, 2025"
  const human = a?.date || null;
  if (human) {
    const t = Date.parse(human);
    if (!Number.isNaN(t)) return t;
  }

  // Unknown dates go last
  return 0;
};

/* =========================
   MOTION VARIANTS
========================= */
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

/* =========================
   NEWSLETTER (WEBHOOK)
========================= */
const NewsletterCard = () => {
  const MAKE_NEWSLETTER_WEBHOOK_URL = "https://hook.eu2.make.com/o6vo9qowjwmrxyg4txpx6w4coi2468ho";

  const [email, setEmail] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [status, setStatus] = useState({ sending: false, success: false, error: "" });

  const resetStatus = () => setStatus({ sending: false, success: false, error: "" });

  const submit = async () => {
    resetStatus();

    // Honeypot triggered -> silently succeed
    if (company) {
      setStatus({ sending: false, success: true, error: "" });
      setEmail("");
      return;
    }

    const cleanEmail = String(email || "").trim().toLowerCase();
    if (!cleanEmail || !cleanEmail.includes("@")) {
      setStatus({ sending: false, success: false, error: "Please enter a valid email." });
      return;
    }

    if (!MAKE_NEWSLETTER_WEBHOOK_URL || MAKE_NEWSLETTER_WEBHOOK_URL.includes("XXXXXXXX")) {
      setStatus({
        sending: false,
        success: false,
        error:
          "Something is wrong.",
      });
      return;
    }

    setStatus({ sending: true, success: false, error: "" });

    try {
      const payload = {
        email: cleanEmail,
        source: "D10 Journal - Newsletter",
        page: typeof window !== "undefined" ? window.location.href : "",
        submittedAt: new Date().toISOString(),
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        language: typeof navigator !== "undefined" ? navigator.language : "",
      };

      const res = await fetch(MAKE_NEWSLETTER_WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Webhook failed (${res.status}). ${text || "No response body."}`);
      }

      setStatus({ sending: false, success: true, error: "" });
      setEmail("");
    } catch (err) {
      setStatus({
        sending: false,
        success: false,
        error: err?.message || "Something went wrong. Please try again.",
      });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  return (
    <div className="bg-[#0F172A] p-8 rounded-3xl text-white text-center relative overflow-hidden">
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#0087E6]/20 rounded-full blur-2xl" />

      <Mail size={32} className="text-[#0087E6] mx-auto mb-4" />
      <h3 className="font-anton text-2xl uppercase mb-2">Join The Squad</h3>
      <p className="font-serif text-white/60 text-sm mb-6">
        Get D10 stories delivered to your inbox.
      </p>

      {status.success && (
        <div className="mb-4 rounded-xl border border-[#00D4AA]/30 bg-[#00D4AA]/10 p-3 text-left">
          <p className="text-sm font-bold">✅ You’re in.</p>
          <p className="text-xs text-white/70">We’ll send you the good stuff.</p>
        </div>
      )}

      {status.error && (
        <div className="mb-4 rounded-xl border border-red-400/20 bg-red-500/10 p-3 text-left">
          <p className="text-sm font-bold">Oops</p>
          <p className="text-xs text-white/70">{status.error}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {/* Honeypot */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="hidden"
          aria-hidden="true"
        />

        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={status.sending}
          className="w-full bg-white/10 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#0087E6]"
        />

        <button
          type="button"
          onClick={submit}
          disabled={status.sending}
          className={`w-full font-bold uppercase text-xs tracking-widest py-3 rounded-lg transition-colors
            ${
              status.sending
                ? "bg-[#0087E6]/60 text-white/80 cursor-not-allowed"
                : "bg-[#0087E6] text-white hover:bg-white hover:text-[#0F172A]"
            }`}
        >
          {status.sending ? "Subscribing..." : "Subscribe"}
        </button>

        
      </div>
    </div>
  );
};

const BlogHero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  if (!FEATURED_STORY) return null;

  return (
    <Link to={`/blog/${FEATURED_STORY.slug}`} className="block">
      <header
        ref={containerRef}
        className="relative w-full h-[85vh] min-h-[600px] overflow-hidden bg-[#0F172A] group cursor-pointer"
      >
        <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
          <img
            src={FEATURED_STORY.heroImage || FEATURED_STORY.image}
            alt={FEATURED_STORY.title}
            className="w-full h-full object-cover opacity-90 transition-transform duration-1000 group-hover:scale-105"
            loading="eager"
            fetchpriority="high"
          />
        </motion.div>

        <div className="absolute inset-0 z-[1] bg-gradient-to-t from-[#0F172A] via-transparent to-[#0F172A]/30" />
        <div className="absolute inset-0 z-[1] bg-gradient-to-r from-[#0F172A]/80 via-transparent to-transparent" />

        <div className="absolute inset-0 z-10 flex flex-col justify-end pb-12 sm:pb-20 px-4 sm:px-6">
          <div className="container mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl"
            >
              <div className="inline-flex items-center gap-2 bg-[#0087E6] text-white px-3 py-1 text-[10px] font-bold uppercase tracking-widest mb-6">
                <Star size={12} className="fill-white" />
                {FEATURED_STORY.category || "Cover Story"}
              </div>

              <h1 className="font-anton text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white uppercase leading-[0.9] mb-6 drop-shadow-lg">
                {FEATURED_STORY.title}
              </h1>

              {FEATURED_STORY.excerpt && (
                <p className="font-serif text-white/90 text-lg sm:text-xl md:text-2xl max-w-2xl mb-8 leading-relaxed">
                  {FEATURED_STORY.excerpt}
                </p>
              )}

              <div className="flex flex-wrap items-center gap-6 text-white/70 text-xs sm:text-sm font-manrope font-bold uppercase tracking-widest border-t border-white/20 pt-6">
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-[#0087E6]" />
                  By {FEATURED_STORY.author || "D10 Editorial"}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={14} className="text-[#0087E6]" />
                  {FEATURED_STORY.date || ""}
                </div>
                <div className="flex items-center gap-2 text-white">
                  <Clock size={14} className="text-[#FFD700]" />
                  {FEATURED_STORY.readTime || ""}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </header>
    </Link>
  );
};

/* =========================
   PRESS ROOM
========================= */
const PressRoom = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <section id="press" className="bg-white" ref={ref}>
      <div className="w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-10 gap-4"
        >
          <div>
            <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs uppercase tracking-widest bg-blue-50 px-3 py-1.5 rounded-full mb-3">
              Press
            </span>
            <h2 className="text-[#0F172A] font-anton text-3xl sm:text-4xl uppercase leading-none">
              Press Room
            </h2>
          </div>
          <p className="font-serif text-gray-500 sm:max-w-xs sm:text-right text-sm">
            Articles and coverage about D10&apos;s Pizza Fantasista.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 gap-4"
        >
          {PRESS_LINKS.map((link, i) => (
            <motion.a
              key={i}
              href={link.url}
              target="_blank"
              rel="noreferrer"
              variants={fadeInUp}
              className="group bg-[#F0F9FF] rounded-2xl hover:shadow-lg hover:bg-white transition-all duration-300 border border-blue-100 overflow-hidden"
            >
              <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr]">
                {/* Thumbnail */}
                <div className="relative">
                  <div className="aspect-[16/10] sm:aspect-auto sm:h-full w-full overflow-hidden">
                    <img
                      src={link.image}
                      alt={link.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/35 via-transparent to-transparent" />
                  </div>

                  {/* Logo chip */}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-2 bg-white/90 backdrop-blur px-2.5 py-1.5 rounded-full border border-white/60 shadow-sm">
                    {link.logo ? (
                      <img
                        src={link.logo}
                        alt={`${link.source} logo`}
                        className="h-4 w-auto"
                        loading="lazy"
                      />
                    ) : (
                      <span className="font-anton text-xs uppercase text-[#0087E6]">
                        {link.source}
                      </span>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 sm:p-6 flex flex-col justify-between min-h-[140px]">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                        {link.date}
                      </span>
                      <h3 className="font-serif text-lg sm:text-xl text-[#0F172A] leading-snug group-hover:text-[#0087E6] transition-colors mt-2">
                        &quot;{link.title}&quot;
                      </h3>
                    </div>

                    <ExternalLink
                      size={16}
                      className="text-[#0087E6]/30 group-hover:text-[#0087E6] transition-colors flex-shrink-0 mt-1"
                    />
                  </div>

                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400 mt-5">
                    <span className="text-[#0087E6]">{link.source}</span>
                    <span className="h-px w-3 bg-gray-300" />
                    <span className="group-hover:text-[#0087E6] transition-colors">
                      Read
                    </span>
                  </div>
                </div>
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

const EditorialFeed = () => {
  const [activeCategory, setActiveCategory] = useState("All Stories");
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  // ✅ Build one consistent list for filters + feed, including FEATURED_STORY
  const ALL_STORIES = useMemo(() => buildAllStories(), []);

  // ✅ Categories must reflect ALL_STORIES (and still allow Press)
  const categories = useMemo(() => {
    const base = typeof getCategories === "function" ? getCategories() : ["All Stories"];

    const catSet = new Set(
      (Array.isArray(base) ? base : ["All Stories"]).map(normalizeCategory)
    );

    catSet.add("All Stories");
    if (FEATURED_STORY?.category) catSet.add(normalizeCategory(FEATURED_STORY.category));

    ALL_STORIES.forEach((a) => {
      if (a?.category) catSet.add(normalizeCategory(a.category));
    });

    catSet.add("Press");

    const list = Array.from(catSet).filter(Boolean);
    const allFirst = list.filter((c) => c === "All Stories");
    const pressLast = list.filter((c) => c === "Press");
    const rest = list.filter((c) => c !== "All Stories" && c !== "Press");
    rest.sort((a, b) => a.localeCompare(b));

    return [...allFirst, ...rest, ...pressLast];
  }, [ALL_STORIES]);
// ✅ NEW: Always sort by date (newest first), then filter
const filteredArticles = useMemo(() => {
  if (activeCategory === "Press") return [];

  const sorted = [...ALL_STORIES].sort((a, b) => getSortTs(b) - getSortTs(a));

  if (activeCategory === "All Stories") return sorted;

  return sorted.filter((a) => isSameCategory(a.category, activeCategory));
}, [activeCategory, ALL_STORIES]);

// ✅ NEW: Trending also uses the same date sort
const trending = useMemo(() => {
  const sorted = [...ALL_STORIES].sort((a, b) => getSortTs(b) - getSortTs(a));
  return sorted.slice(0, 4);
}, [ALL_STORIES]);

  const showPressOnly = activeCategory === "Press";
  const showPressBelowArticles = activeCategory === "All Stories";

  return (
    <section className="py-20 bg-white" ref={ref}>
      <div className="container mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 border-b border-gray-100 pb-8"
        >
          <div>
            <span className="text-[#0087E6] font-manrope font-bold text-[10px] sm:text-xs uppercase tracking-[0.2em] mb-2 block">
              D10 Journal
            </span>
            <h2 className="font-anton text-4xl sm:text-6xl text-[#0F172A] uppercase leading-none">
              The Journal
            </h2>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 md:pb-0">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-all ${
                  activeCategory === cat
                    ? "bg-[#0F172A] text-white"
                    : "bg-[#F0F9FF] text-gray-500 hover:bg-[#0087E6] hover:text-white"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* LEFT */}
          <div className="lg:col-span-8 grid gap-12">
            {showPressOnly ? (
              <PressRoom />
            ) : (
              <>
                {filteredArticles.map((article, i) => (
                  <Link key={article.slug} to={`/blog/${article.slug}`} className="block">
                    <motion.article
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ delay: i * 0.07 }}
                      className="group cursor-pointer grid md:grid-cols-2 gap-6 md:gap-8 items-center border-b border-gray-100 pb-12 last:border-0"
                    >
                      <div className="relative aspect-[4/3] md:aspect-square lg:aspect-[4/3] overflow-hidden rounded-2xl">
                        <img
                          src={article.image || article.heroImage}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />

                        {article.category && (
                          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest text-[#0F172A]">
                            {article.category}
                          </div>
                        )}

                        {FEATURED_STORY?.slug === article.slug && (
                          <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 bg-[#0087E6] text-white px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest shadow">
                            <Star size={12} className="fill-white" />
                            Featured
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col h-full justify-center">
                        <div className="flex items-center gap-3 mb-4 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                          <span className="text-[#0087E6]">{article.date || ""}</span>
                          <span>•</span>
                          <span>{article.readTime || ""}</span>
                        </div>

                        <h3 className="font-anton text-2xl sm:text-3xl md:text-4xl text-[#0F172A] uppercase leading-[0.95] mb-4 group-hover:text-[#0087E6] transition-colors">
                          {article.title}
                        </h3>

                        {article.excerpt && (
                          <p className="font-serif text-gray-600 text-lg mb-6 line-clamp-3">
                            {article.excerpt}
                          </p>
                        )}

                        <div className="mt-auto flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#0F172A] group-hover:translate-x-2 transition-transform">
                          Read Full Story{" "}
                          <ArrowRight size={14} className="text-[#0087E6]" />
                        </div>
                      </div>
                    </motion.article>
                  </Link>
                ))}

                {!filteredArticles.length && (
                  <div className="text-gray-500 font-manrope">
                    No stories in this category yet.
                  </div>
                )}

                {showPressBelowArticles && (
                  <div className="pt-6 border-t border-gray-100">
                    <PressRoom />
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT */}
          <div className="lg:col-span-4 space-y-12">
            <div className="bg-[#F0F9FF] p-6 rounded-3xl">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp size={18} className="text-[#0087E6]" />
                <span className="font-anton text-xl uppercase text-[#0F172A]">
                  Trending Now
                </span>
              </div>

              <div className="space-y-6">
                {trending.map((a, idx) => (
                  <Link key={a.slug} to={`/blog/${a.slug}`} className="block">
                    <div className="flex gap-4 items-start group">
                      <span className="font-anton text-2xl text-[#0087E6]/30 group-hover:text-[#0087E6] transition-colors">
                        {String(idx + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <h4 className="font-manrope font-bold text-sm text-[#0F172A] leading-tight mb-1 group-hover:text-[#0087E6] transition-colors">
                          {a.title}
                        </h4>
                        <span className="text-[10px] uppercase tracking-widest text-gray-400">
                          {(a.category || "Story")} • {(a.readTime || "")}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* ✅ Newsletter card with webhook */}
            <NewsletterCard />
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Blog() {
  const canonical = `${SITE.baseUrl}/blog`;
  const ogImage = FEATURED_STORY?.heroImage || FEATURED_STORY?.image || "";

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: SITE.blogTitle,
    description: SITE.blogDescription,
    url: canonical,
    isPartOf: { "@type": "WebSite", name: SITE.name, url: SITE.baseUrl },
  };

  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden selection:bg-[#0087E6] selection:text-white">
      <Helmet>
        <title>
          {SITE.blogTitle} | {SITE.name}
        </title>
        <link rel="canonical" href={canonical} />
        <meta name="description" content={SITE.blogDescription} />

        <meta property="og:title" content={`${SITE.blogTitle} | ${SITE.name}`} />
        <meta property="og:description" content={SITE.blogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        {ogImage && <meta property="og:image" content={ogImage} />}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${SITE.blogTitle} | ${SITE.name}`} />
        <meta name="twitter:description" content={SITE.blogDescription} />
        {ogImage && <meta name="twitter:image" content={ogImage} />}

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&family=Playfair+Display:ital,wght@0,400;1,400;1,700&display=swap');
        .font-anton { font-family: 'Anton', sans-serif; }
        .font-manrope { font-family: 'Manrope', sans-serif; }
        .font-serif { font-family: 'Playfair Display', serif; }
        html { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 8px; }
        ::-webkit-scrollbar-track { background: #F0F9FF; }
        ::-webkit-scrollbar-thumb { background: #0087E6; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #0F172A; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <Navbar />
      <BlogHero />
      <EditorialFeed />
      <Footer />
    </div>
  );
}
